
import React, { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import type { Canvas } from '@/hooks/useCanvas'

interface CanvasDuplicateModalProps {
  isOpen: boolean
  onClose: () => void
  canvas: Canvas | null
  onDuplicate: (canvasId: string, newTitle: string) => Promise<void>
}

export const CanvasDuplicateModal: React.FC<CanvasDuplicateModalProps> = ({
  isOpen,
  onClose,
  canvas,
  onDuplicate,
}) => {
  const [title, setTitle] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  React.useEffect(() => {
    if (canvas) {
      setTitle(`${canvas.title} (Copy)`)
    }
  }, [canvas])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canvas || !title.trim()) return

    setIsLoading(true)
    try {
      await onDuplicate(canvas.uuid, title.trim())
      onClose()
    } catch (error) {
      console.error('Failed to duplicate canvas:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Session</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a name for the duplicated session
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={cn(
                'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
                'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                'placeholder:text-muted-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
              placeholder="Session title"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel type="button" disabled={isLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="submit"
              disabled={isLoading || !title.trim()}
            >
              {isLoading ? (
                <>
                  <Icon name="loader-2" className="mr-2 h-4 w-4 animate-spin" />
                  Duplicating...
                </>
              ) : (
                'Duplicate'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  )
}